// =====================================================
// FORMAT SEGMENTS — map a diff piece (whole word or sub-word) onto the per-run
// formatting of its source word, so a bold defined term keeps its non-bold
// quotes/parens in every emit path (.docx track changes, .docx markup, preview).
// =====================================================

import type { RichToken } from '../types';
import { normalizeQuotes } from './diff-engine-core';

export type Segment = { text: string; rPrXml: string };

/**
 * The per-format-run pieces of `value`, a diff piece drawn from `token`'s word.
 *
 * - Whole word (`value === token.word`): the token's own segments.
 * - Sub-word piece (intra-word refinement, e.g. `"Declaration` + `s` + `")`): the
 *   segments clipped to the piece's character range. `charIdx` is the piece's offset
 *   within the word (set by the refiner); without it the piece is located by a unique
 *   substring match, and an ambiguous or non-matching piece returns null.
 *
 * Returns null when the word is a single run (the caller's single-rPr path is already
 * right) or the piece can't be placed. A one-element result is meaningful: the piece
 * lies entirely inside one run, which may NOT be the run the whole word's rPr came from.
 */
export function pieceSegments(token: RichToken | undefined, value: string, charIdx?: number): Segment[] | null {
  if (!token || !token.segments || token.segments.length < 2 || !value) return null;
  const word = token.word;
  if (value === word) return token.segments;
  // The piece may come from the OTHER side of an equal pair whose quotes/dashes differ
  // only in style (curly vs straight — the diff treats those as equal): match the
  // character range by normalized text, then slice the pieces from `value` itself.
  const same = (a: string, b: string) => a === b || (a.length === b.length && normalizeQuotes(a) === normalizeQuotes(b));
  let start = charIdx;
  if (start == null || start < 0 || !same(word.slice(start, start + value.length), value)) {
    if (same(word, value)) start = 0;
    else {
      const first = word.indexOf(value);
      if (first < 0 || word.indexOf(value, first + 1) >= 0) return null;
      start = first;
    }
  }
  const end = start + value.length;
  const out: Segment[] = [];
  let off = 0;
  for (const seg of token.segments) {
    const segStart = off, segEnd = off + seg.text.length;
    off = segEnd;
    const a = Math.max(start, segStart), b = Math.min(end, segEnd);
    if (b > a) out.push({ text: value.slice(a - start, b - start), rPrXml: seg.rPrXml });
  }
  return out.length ? out : null;
}

/**
 * Align the revised and original per-run formatting of the SAME text (an equal word or
 * piece) into intervals that each carry one revised rPr and one original rPr, so the
 * emitter can track a formatting change per run instead of forcing the whole word into
 * one rPr + one rPrChange (which made a bold term's non-bold `")` bold). Adjacent
 * intervals with identical formatting on both sides are merged. Returns null when the
 * two sides' text doesn't line up.
 */
export function pairSegments(revSegs: Segment[], origSegs: Segment[]): { text: string; rPrXml: string; origRPrXml: string }[] | null {
  const text = revSegs.map(s => s.text).join('');
  const origText = origSegs.map(s => s.text).join('');
  if (origText.length !== text.length) return null;
  const cuts = new Set<number>([0, text.length]);
  let off = 0; for (const s of revSegs) { off += s.text.length; cuts.add(off); }
  off = 0; for (const s of origSegs) { off += s.text.length; cuts.add(off); }
  const sorted = [...cuts].sort((a, b) => a - b);
  const at = (segs: Segment[], pos: number): string => {
    let o = 0;
    for (const s of segs) { if (pos < o + s.text.length) return s.rPrXml; o += s.text.length; }
    return segs[segs.length - 1].rPrXml;
  };
  const out: { text: string; rPrXml: string; origRPrXml: string }[] = [];
  for (let i = 0; i + 1 < sorted.length; i++) {
    const a = sorted[i], b = sorted[i + 1];
    if (b <= a) continue;
    const piece = { text: text.slice(a, b), rPrXml: at(revSegs, a), origRPrXml: at(origSegs, a) };
    const last = out[out.length - 1];
    if (last && last.rPrXml === piece.rPrXml && last.origRPrXml === piece.origRPrXml) last.text += piece.text;
    else out.push(piece);
  }
  return out.length ? out : null;
}
